// Thin WebGL2 helpers — context, program linking, static mesh (pos + normal + indices).

export function createContext(canvas: HTMLCanvasElement): WebGL2RenderingContext {
  // alpha: the canvas composites over the page's sky-gradient background.
  const gl = canvas.getContext("webgl2", {
    antialias: true,
    alpha: true,
    premultipliedAlpha: true,
    powerPreference: "high-performance",
  });
  if (!gl) throw new Error("WebGL2 not supported");
  return gl;
}

function compileShader(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type);
  if (!sh) throw new Error("Failed to create shader");
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(sh) ?? "(no log)";
    gl.deleteShader(sh);
    throw new Error(`Shader compile error:\n${info}\n\n--- source ---\n${src}`);
  }
  return sh;
}

export function createProgram(
  gl: WebGL2RenderingContext,
  vsSrc: string,
  fsSrc: string,
): WebGLProgram {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vsSrc);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSrc);
  const p = gl.createProgram();
  if (!p) throw new Error("Failed to create program");
  gl.attachShader(p, vs);
  gl.attachShader(p, fs);
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    const info = gl.getProgramInfoLog(p) ?? "(no log)";
    throw new Error(`Program link error:\n${info}`);
  }
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  return p;
}

export interface Mesh {
  vao: WebGLVertexArrayObject;
  indexCount: number;
  dispose(): void;
}

export interface MeshData {
  positions: Float32Array; // location 0, vec3
  normals: Float32Array; // location 1, vec3
  uvs: Float32Array; // location 2, vec2 (face-local, for cell seams)
  materials: Float32Array; // location 3, float (atlas TileId, render/onion.ts)
  tints: Float32Array; // location 4, float 0|1 (1 = infected)
  indices: Uint16Array;
}

function attribBuffer(
  gl: WebGL2RenderingContext,
  location: number,
  data: Float32Array,
  size: number,
): WebGLBuffer {
  const buf = gl.createBuffer();
  if (!buf) throw new Error("Failed to create buffer");
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(location);
  gl.vertexAttribPointer(location, size, gl.FLOAT, false, 0, 0);
  return buf;
}

export function createMesh(gl: WebGL2RenderingContext, data: MeshData): Mesh {
  const vao = gl.createVertexArray();
  if (!vao) throw new Error("Failed to create VAO");
  gl.bindVertexArray(vao);

  const buffers = [
    attribBuffer(gl, 0, data.positions, 3),
    attribBuffer(gl, 1, data.normals, 3),
    attribBuffer(gl, 2, data.uvs, 2),
    attribBuffer(gl, 3, data.materials, 1),
    attribBuffer(gl, 4, data.tints, 1),
  ];

  const iBuf = gl.createBuffer();
  if (!iBuf) throw new Error("Failed to create buffer");
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, iBuf);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, data.indices, gl.STATIC_DRAW);
  buffers.push(iBuf);

  gl.bindVertexArray(null);
  return {
    vao,
    indexCount: data.indices.length,
    dispose() {
      gl.deleteVertexArray(vao);
      for (const b of buffers) gl.deleteBuffer(b);
    },
  };
}
